import React, { useState, useEffect } from "react";
import moment from "moment";

import { makeStyles } from "@material-ui/core/styles";
import { Paper, Grid, Typography, Button } from "@material-ui/core/";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";

import { ProfileChip, RoomChip, EventItem, StatusChip, UserComment, DateTimeView, DocumentView } from "../../general";

import lastStatus from "../../../utils/lastStatus";
import localization from "../../../utils/localizations";
import { Document, Schedule, Thesis, ThesisDefense } from "../../../types";

const useStyles = makeStyles((theme) => ({
  mainBody: {
    padding: 10,
  },
  mainGrid: {
    marginTop: 20,
  },
  gridLabelBolder: {
    textAlign: "right",
    fontWeight: 500,
  },
  profileSpacing: {
    marginRight: 10,
  },
  marginVertical: {
    marginTop: 10,
    marginBottom: 10,
  },
}));

function ThesisDefenseDetail(props: { thesisDefense: ThesisDefense }) {
  const classes = useStyles();
  let lastSubmittedStatus = lastStatus(props.thesisDefense.status_history);
  let lastUpdatedStatus = lastStatus(props.thesisDefense.status_history, props.thesisDefense.status);
  return (
    <div>
      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>
            {localization.title}
          </Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <Typography variant="body1">{props.thesisDefense.title}</Typography>
        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>
            {localization.status}
          </Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <StatusChip status={props.thesisDefense.status} size="small" />
          {lastUpdatedStatus?.comment && (
            <div>
              <Typography variant="subtitle2" className={classes.marginVertical}>
                {localization.comment}
              </Typography>
              <UserComment comment={lastUpdatedStatus.comment} inline />
            </div>
          )}
        </Grid>
      </Grid>

      {lastSubmittedStatus && (
        <Grid container spacing={3} className={classes.mainGrid}>
          <Grid item xs={4} sm={3}>
            <Typography variant="body1" className={classes.gridLabelBolder}>
              {localization.lastUpdated}
            </Typography>
          </Grid>
          <Grid item xs={8} sm={9}>
            <Typography variant="body1">
              <DateTimeView value={lastSubmittedStatus.updated_at} chip />
            </Typography>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>
            {localization.advisor} 1
          </Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          {props.thesisDefense.thesis.primary_advisor && <ProfileChip profile={props.thesisDefense.thesis.primary_advisor} />}
        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>
            {localization.additionalAdvisors}
          </Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          {props.thesisDefense.thesis.additional_advisors.map((advisor, i) => {
            return <ProfileChip key={`advisor-${i}`} profile={advisor} className={classes.profileSpacing} />;
          })}
        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>
            {localization.examiner}
          </Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          {props.thesisDefense.examiners.map((examiner, i) => {
            return <ProfileChip key={`examiner-${i}`} profile={examiner} className={classes.profileSpacing} />;
          })}
        </Grid>
      </Grid>

      {props.thesisDefense.documents.map((doc: Document, j: number) => {
        let label = (localization as any)[doc.kind] ? (localization as any)[doc.kind] : doc.kind;
        let prevDoc = j > 0 ? props.thesisDefense.documents[j - 1] : undefined;
        if (prevDoc && prevDoc.kind == doc.kind) label = "";
        return (
          <Grid container spacing={3} className={classes.mainGrid}>
            <Grid item xs={4} sm={3}>
              <Typography variant="body1" className={classes.gridLabelBolder}>
                {label}
              </Typography>
            </Grid>
            <Grid item xs={8} sm={9}>
              <DocumentView doc={doc} />
            </Grid>
          </Grid>
        );
      })}

      {props.thesisDefense.schedules.length > 0 && (
        <Grid container spacing={3} className={classes.mainGrid}>
          <Grid item xs={4} sm={3}>
            <Typography variant="body1" className={classes.gridLabelBolder}>
              {localization.schedule}
            </Typography>
          </Grid>
          <Grid item xs={8} sm={9}>
            {props.thesisDefense.schedules.map((event: Schedule, j: number) => {
              return <EventItem key={`schedule-event-${j}`} event={event} />;
            })}
          </Grid>
        </Grid>
      )}

      {props.thesisDefense.thesis_defense_schedules.length > 0 && (
        <Grid container spacing={3} className={classes.mainGrid}>
          <Grid item xs={4} sm={3}>
            <Typography variant="body1" className={classes.gridLabelBolder}>
              {localization.schedule}
            </Typography>
          </Grid>
          <Grid item xs={8} sm={9}>
            {props.thesisDefense.thesis_defense_schedules.map((event: Schedule, j: number) => {
              return <EventItem key={`schedule-event-${j}`} event={event} />;
            })}
          </Grid>
        </Grid>
      )}
    </div>
  );
}

export { ThesisDefenseDetail };
